// This week's training plan: read it, make it the first time it's needed,
// log a session in one tap, swap a skill.

import { useEffect } from 'react';
import { ageNow } from '../domain/age.ts';
import { pickWeek, swapSkill, weekStart } from '../domain/trainingplan.ts';
import { MINUTE, today } from '../domain/time.ts';
import type { LogEvent, TrainingWeek } from '../domain/types.ts';
import { logEvent } from './actions.ts';
import { useCollection, useDevice, useDoc, useNow, useProfile } from './hooks.ts';
import { data } from './index.ts';

/** The plan for the current week, made the first time any owner's device needs it (once he's home). */
export function useTrainingWeek(): TrainingWeek | undefined {
  const now = useNow(5 * 60_000);
  const id = weekStart(now);
  const plan = useDoc('trainingWeeks', id);
  const profile = useProfile();
  const progress = useCollection('skills');
  const guest = !!useDevice().guest;
  const home = !profile.homeDate || profile.homeDate <= today(now);
  const weeks = ageNow(profile, now)?.weeks;
  useEffect(() => {
    if (plan || guest || !home || weeks === undefined) return;
    const skills = pickWeek(weeks, progress);
    // A weak write: if another device already made this week's plan (and this one
    // hasn't synced it yet), theirs wins instead of being replaced mid-week.
    if (skills.length) data().store.putMany('trainingWeeks', [{ id, skills }], { weak: true });
  }, [plan, guest, home, weeks, id, progress]);
  return home ? plan : undefined;
}

/** One five-minute session on this week's skills. */
export function logTrainingSession(plan: TrainingWeek, skills = plan.skills): LogEvent<'training'> {
  const now = Date.now();
  return logEvent('training', { skills }, { at: now - 5 * MINUTE, end: now });
}

export function swapPlanSkill(plan: TrainingWeek, out: string): void {
  const { store } = data();
  const profile = store.get('profile', 'arlo');
  const weeks = profile ? ageNow(profile, Date.now())?.weeks : undefined;
  if (weeks === undefined) return;
  store.put('trainingWeeks', swapSkill(plan, out, weeks, store.list('skills')));
}
